import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchTruthUtilisationJobs,
  fetchTruthUtilisationStatus,
  triggerTruthUtilisationCompute,
  utilisationLastComputedMeta,
} from '../services/utilisationTruthClient';
import type {
  TruthUtilisationJobRecord,
  TruthUtilisationStatusResponse,
} from '../types/utilisationTruth';

export interface UseTruthUtilisationPanelOptions {
  startDate: string;
  endDate: string;
  onDataRefresh?: () => void;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

export function useTruthUtilisationPanel({
  startDate,
  endDate,
  onDataRefresh,
}: UseTruthUtilisationPanelOptions) {
  const [statusData, setStatusData] = useState<TruthUtilisationStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [computeJobs, setComputeJobs] = useState<TruthUtilisationJobRecord[]>([]);
  const [computeJobsLoading, setComputeJobsLoading] = useState(false);
  const [computeTriggerLoading, setComputeTriggerLoading] = useState(false);
  const [computeTriggerError, setComputeTriggerError] = useState<string | null>(null);
  const [recomputeWarning, setRecomputeWarning] = useState(false);
  const [showJobsPanel, setShowJobsPanel] = useState(false);

  const jobsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevJobsRef = useRef<TruthUtilisationJobRecord[]>([]);
  const statusRequestGenRef = useRef(0);
  const statusAbortRef = useRef<AbortController | null>(null);

  const refreshStatus = useCallback(async () => {
    statusAbortRef.current?.abort();
    const ac = new AbortController();
    statusAbortRef.current = ac;
    const requestId = ++statusRequestGenRef.current;
    const forStart = startDate;
    const forEnd = endDate;

    setStatusLoading(true);
    setStatusError(null);
    setStatusData(null);

    try {
      const status = await fetchTruthUtilisationStatus(forStart, forEnd, { signal: ac.signal });
      if (requestId !== statusRequestGenRef.current) return;
      setStatusData(status);
    } catch (err) {
      if (requestId !== statusRequestGenRef.current || isAbortError(err) || ac.signal.aborted) {
        return;
      }
      setStatusError(err instanceof Error ? err.message : 'Failed to load utilisation status');
      setStatusData(null);
    } finally {
      if (requestId === statusRequestGenRef.current) {
        setStatusLoading(false);
      }
    }
  }, [startDate, endDate]);

  const refreshJobs = useCallback(async () => {
    try {
      const jobs = await fetchTruthUtilisationJobs();
      setComputeJobs(jobs);
      return jobs;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    return () => {
      statusAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setRecomputeWarning(false);
  }, [startDate, endDate]);

  useEffect(() => {
    void refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    const hasActive = computeJobs.some((j) => j.status === 'pending' || j.status === 'running');

    const prevActiveIds = new Set(
      prevJobsRef.current
        .filter((j) => j.status === 'running' || j.status === 'pending')
        .map((j) => j.jobId),
    );
    const justCompleted = computeJobs.filter(
      (j) =>
        (j.status === 'completed' || j.status === 'failed') && prevActiveIds.has(j.jobId),
    );
    if (justCompleted.length > 0) {
      onDataRefresh?.();
      void refreshStatus();
    }
    prevJobsRef.current = computeJobs;

    if (hasActive && !jobsPollRef.current) {
      jobsPollRef.current = setInterval(() => {
        void refreshJobs();
      }, 3000);
    } else if (!hasActive && jobsPollRef.current) {
      clearInterval(jobsPollRef.current);
      jobsPollRef.current = null;
    }

    return () => {
      if (jobsPollRef.current) {
        clearInterval(jobsPollRef.current);
        jobsPollRef.current = null;
      }
    };
  }, [computeJobs, refreshJobs, onDataRefresh, refreshStatus]);

  const doCompute = useCallback(async () => {
    setRecomputeWarning(false);
    setComputeTriggerLoading(true);
    setComputeTriggerError(null);
    try {
      await triggerTruthUtilisationCompute(startDate, endDate);
      await refreshJobs();
      setShowJobsPanel(true);
    } catch (err) {
      setComputeTriggerError(err instanceof Error ? err.message : 'Computation failed');
    } finally {
      setComputeTriggerLoading(false);
    }
  }, [startDate, endDate, refreshJobs]);

  const conflictingCompletedJob = useMemo(
    () =>
      computeJobs.find(
        (j) => j.status === 'completed' && j.startDate === startDate && j.endDate === endDate,
      ) ?? null,
    [computeJobs, startDate, endDate],
  );

  const handleCompute = useCallback(() => {
    if (conflictingCompletedJob) {
      setRecomputeWarning(true);
      return;
    }
    void doCompute();
  }, [conflictingCompletedJob, doCompute]);

  const hasActiveJob = computeJobs.some((j) => j.status === 'pending' || j.status === 'running');

  const lastComputed = useMemo(
    () => utilisationLastComputedMeta(statusData?.last_computed ?? null),
    [statusData],
  );

  return {
    statusData,
    statusLoading,
    statusError,
    lastComputed,
    computeJobs,
    computeJobsLoading,
    computeTriggerLoading,
    computeTriggerError,
    recomputeWarning,
    showJobsPanel,
    setShowJobsPanel,
    hasActiveJob,
    conflictingCompletedJob,
    handleCompute,
    doCompute,
    setRecomputeWarning,
    refreshStatus,
    refreshJobs: async () => {
      setComputeJobsLoading(true);
      try {
        return await refreshJobs();
      } finally {
        setComputeJobsLoading(false);
      }
    },
  };
}
